import { useMemo, useState } from "react";
import { IconHelp } from "./icons";
import styles from "./SpecsPanel.module.css";

// Right-rail "SPECS" block.
//
// Shape (mirrors the NetSuite custom-fields harvest):
//   specs = [
//     { label: "Weight",      us: "4.5 lb",        metric: "2.04 kg" },
//     { label: "Volume",      us: "2185 cu-ins",   metric: "35.8 L", hint: "How is volume calculated?" },
//     { label: "Dimensions",  us: '22.75"x14"x12.5"', metric: "57.8x35.6x31.7 cm", hint: "How are dimensions calculated?" },
//     { label: "Frame System",   value: "Futura Frame" },          // unit-agnostic
//     { label: "Harnessing Style", value: "Limited" },
//   ]
//   origin = { label: "Made in the USA", note: "Berry Compliant", flag: "🇺🇸" }
export default function SpecsPanel({ specs = [], origin, defaultUnit = "us" }) {
  const [unit, setUnit] = useState(defaultUnit);
  const hasMetric = useMemo(() => specs.some((s) => s.metric), [specs]);

  if (!specs.length && !origin) return null;

  return (
    <aside className={styles.panel} aria-label="Specs">
      <div className={styles.head}>
        <h2 className={styles.heading}>SPECS</h2>
        {hasMetric && (
          <div className={styles.toggle} role="tablist" aria-label="Units">
            <button
              type="button"
              role="tab"
              aria-selected={unit === "us"}
              className={[styles.toggleBtn, unit === "us" ? styles.toggleBtnActive : ""].join(" ")}
              onClick={() => setUnit("us")}
            >
              US
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={unit === "metric"}
              className={[styles.toggleBtn, unit === "metric" ? styles.toggleBtnActive : ""].join(" ")}
              onClick={() => setUnit("metric")}
            >
              Metric
            </button>
          </div>
        )}
      </div>
      <div className={styles.divider} />

      <dl className={styles.list}>
        {specs.map((s, i) => {
          const v = pickValue(s, unit);
          if (v == null) return null;
          return (
            <div key={`${s.label}-${i}`} className={styles.row}>
              <dt className={styles.label}>{s.label}</dt>
              <dd className={styles.value}>{v}</dd>
              {s.hint && (
                <button type="button" className={styles.hint}>
                  <IconHelp size={14} />
                  <span>{s.hint}</span>
                </button>
              )}
            </div>
          );
        })}
      </dl>

      {origin && (
        <>
          <div className={styles.divider} />
          <div className={styles.origin}>
            <div className={styles.originText}>
              <strong>{origin.label}</strong>
              {origin.note && <span className={styles.originNote}>{origin.note}</span>}
            </div>
            {origin.flag && <span className={styles.originFlag} aria-hidden>{origin.flag}</span>}
          </div>
        </>
      )}
    </aside>
  );
}

function pickValue(s, unit) {
  if (unit === "metric" && s.metric != null) return s.metric;
  if (unit === "us" && s.us != null) return s.us;
  return s.value ?? s.us ?? s.metric ?? null;
}
